import { Component, OnDestroy, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { concat, of, from, Subscription, Subject } from 'rxjs';
import { map, concatMap, filter, switchMap, debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';

import { Router } from '@angular/router';

import * as XLSX from 'xlsx';

import { environment } from 'src/environments/environment';
import date from 'date-and-time';
import { round1, round2, ExcelDateToJSDate, parseMyFloat, parseMyFloatEx, parseTextBuffer } from 'src/app/shared/round';

import { Product } from 'src/app/shared/product'
import { Document, DocItems, ImportItemFields } from 'src/app/shared/document';
import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'

import { ProductApiService } from 'src/app/services/product-api.service';
import { DocumentApiService } from 'src/app/services/document-api.service';

/**
 * Modal dialog for importing document line items from an Excel file or tab/CSV text.
 *
 * ### Import flow
 * 1. **File selector** — user uploads an `.xlsx` file (`onFileChange`) or pastes
 *    tab/CSV text (`onInsertText`); `processDataSource` extracts column headers
 *    and advances to the field-selector step.
 * 2. **Field selector** — user maps sheet columns to `ImportItemFields` entries
 *    (`paramsMap`); `canInsertData` guards the Next button.
 * 3. **Import result** — `onDataInsert` iterates `dataSource` rows via
 *    `from + concatMap`, calls `getProductByCode`, builds `DocItems` entries,
 *    and shows the result table.
 * 4. **Confirm** — `onImportContent` posts/puts all items to the API and emits
 *    `importIsDone`.
 *
 * ### Date parsing
 * When any `paramsMap` entry has `fieldType === 'dat'`, `processDateField` is set
 * and `selectedDateProcess` selects from nine date-format strategies
 * (Pure Excel serial, JSON, MM/DD/YYYY, DD/MM/YYYY, DD.MM.YYYY, YYYYMMDD variants, etc.).
 *
 * ### Duplicate handling
 * Controlled by `clearBefore`, `overwriteExisted`, and `addExisted` flags.
 * A row already exists when a `docItems` line has the same `PRODID` as the product the
 * imported code resolved to (lines added earlier in the same import count).
 * `addExisted` accumulates quantity; `overwriteExisted` replaces price/quantity;
 * with neither set, the row is added as a separate line.
 */
@Component({
  selector: 'app-import-excel-dlg',
  templateUrl: './import-excel-dlg.component.html',
  styleUrls: ['./import-excel-dlg.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ImportExcelDlgComponent implements OnInit, OnChanges, OnDestroy {
  private globalSettinsData: GlobalSettingsData;

  /** Controls dialog visibility; two-way bound via `displayChange`. */
  @Input() display: boolean = false;

  /** Dialog header text. */
  @Input() header: string = 'Импорт содержимого документа';

  /** The target document whose items will be replaced or augmented on confirm. */
  @Input() document: Document = null;

  /**
   * Column-mapping definitions for the field-selector step.
   * Each entry declares a `DocItems` field name, display label, mandatory flag,
   * data type, and (after mapping) the resolved Excel column name (`importColumnName`).
   * Defaults cover PRODCODE, DOCITEMPRODNAME, DOCITEMPRICE, DOCITEMQTY,
   * DOCITEMVPRICE, DOCITEMTEXT1, DOCITEMTEXT2, and U4ETKA.
   */
  @Input() paramsMap: ImportItemFields[] = [
    { fieldName: 'PRODCODE', fieldLabel: 'Код товара', fieldIsMandatory: true, fieldType: 'str' },
    { fieldName: 'DOCITEMPRODNAME', fieldLabel: 'Наименование товара', fieldIsMandatory: false, fieldType: 'str' },
    { fieldName: 'DOCITEMPRICE', fieldLabel: 'Цена товара', fieldIsMandatory: true, fieldType: 'num' },
    { fieldName: 'DOCITEMQTY', fieldLabel: 'Количество', fieldIsMandatory: true, fieldType: 'num' },
    { fieldName: 'DOCITEMVPRICE', fieldLabel: 'Цена в валюте', fieldIsMandatory: false, fieldType: 'num' },
    { fieldName: 'DOCITEMTEXT1', fieldLabel: 'Примечание 1', fieldIsMandatory: false, fieldType: 'str' },
    { fieldName: 'DOCITEMTEXT2', fieldLabel: 'Примечание 2', fieldIsMandatory: false, fieldType: 'str' },
    { fieldName: 'U4ETKA', fieldLabel: 'Учетка', fieldIsMandatory: false, fieldType: 'num' },
  ];

  /** When true, existing `docItems` are cleared before the import result is applied. */
  @Input() clearBefore: boolean = true;

  /** When true, a row whose resolved product (`PRODID`) already exists in `docItems` replaces the existing entry. */
  @Input() overwriteExisted: boolean = false;

  /** When true, a row whose resolved product (`PRODID`) already exists in `docItems` accumulates quantity on the existing entry. */
  @Input() addExisted: boolean = true;

  /** When true, the quantity column is visible in the import result table. */
  @Input() showQuantity: boolean = true;

  /** When true, the price column is visible in the import result table. */
  @Input() showPrice: boolean = true;

  /** When true, the first text column (`diText1ColumnLable`) is shown in the result table. */
  @Input() diText1Column: boolean = false;
  /** When true, the second text column (`diText2ColumnLabel`) is shown in the result table. */
  @Input() diText2Column: boolean = false;
  /** Display label for the first text column in the result table. */
  @Input() diText1ColumnLable: string = 'Примечание';
  /** Display label for the second text column in the result table. */
  @Input() diText2ColumnLabel: string = 'Состав';

  diU4ETKAColumnName: string = 'Учетка';

  /** Emits `false` on dialog hide to support two-way `[(display)]` binding. */
  @Output() displayChange = new EventEmitter();

  /** Emits the final `DocItems[]` array after all rows are posted to the API. */
  @Output() importIsDone = new EventEmitter<DocItems[]>();

  modal: boolean = true;

  blocked: boolean = false;
  loading: boolean = false;

  private _documentQuery: Subscription;
  private _getProductQuery: Subscription;
  private _dataSourceQuery: Subscription;
  private _documentPostQuery: Subscription;
  private _productInfoQuery: Subscription;

  private searchStr$: Subject<{ code: string, quantity: number, price: number, u4etka: number }> = new Subject<{ code: string, quantity: number, price: number, u4etka: number }>();

  /** True when the confirm button should be disabled — no items yet or a load is in progress. */
  public get notReady(): boolean { return ((this.docItems.length == 0) || this.loading); }

  textSource: string;

  dataSource: any[];
  columns: string[] = [];

  docItems: DocItems[] = [];

  textType: string = 'tab';   // Either 'csv' so far
  textDelimiter: string = '\t';

  public columnMet(col: string): boolean { return (this.paramsMap.find(e => (e.importColumnName || '') == col)) ? true : false; }

  /** True when every mandatory `paramsMap` field has a mapped Excel column. Guards the import button. */
  public get canInsertData(): boolean {
    let canInsert = true;

    this.paramsMap
      .filter(e => e.fieldIsMandatory)
      .forEach(e => canInsert = canInsert && this.columns.includes(e.importColumnName || ''))
    return canInsert;
  };

  /** True when the pasted text area is non-empty; guards the text-insert button. */
  public get canInsertTextData(): boolean { return (this.textSource || '').trim() != ''; };

  /** True when the U4ETKA field is mapped and its column exists in the current `dataSource`. */
  public get showU4etka(): boolean {
    let col = this.paramsMap.find(e => (e.fieldName) == 'U4ETKA');

    return (col && this.columns && this.columns.includes(col.importColumnName || ''));
  };

  /** True when DOCITEMTEXT1 is mapped and its column exists in the current `dataSource`. */
  public get showDOCITEMTEXT1(): boolean {
    let col = this.paramsMap.find(e => (e.fieldName) == 'DOCITEMTEXT1');

    return (col && this.columns && this.columns.includes(col.importColumnName || ''));
  };

  /** True when DOCITEMTEXT2 is mapped and its column exists in the current `dataSource`. */
  public get showDOCITEMTEXT2(): boolean {
    let col = this.paramsMap.find(e => (e.fieldName) == 'DOCITEMTEXT2');

    return (col && this.columns && this.columns.includes(col.importColumnName || ''));
  };

  openFileSelector: boolean = true;
  openFieldSelector: boolean = false;
  openImportResult: boolean = false;

  processDateField: boolean = false;

  selectedDateProcess: string = 'Pure Excel (44358)';

  dateProcessType: string[] = [
    this.selectedDateProcess,
    'Pure Date (from JSON)',
    'Default Javascript (MM/DD/YYYY)',
    'More usefull (DD/MM/YYYY)',
    'Even More usefull (DD.MM.YYYY)',
    'Trick 1 (20210123 => 2021-01-23)',
    'Trick 2 (20212307 => 2021-07-23)',
    'Trick 3 (2021-07-23 => 2021-07-23)',
    'Don\'t know, honestly'
  ];

  constructor(
    private docApi: DocumentApiService,
    private prodApi: ProductApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private router: Router,
  ) { }

  /**
   * Reacts to `@Input` changes:
   * - `paramsMap` — sets `processDateField` when any entry has `fieldType === 'dat'`.
   * - `document`  — copies `document.items` into `docItems`; adjusts column label names
   *                 for invoice (DOCTYPE 2/3/10) vs. shipment (DOCTYPE 1/4/5/12) flows.
   * - `display`   — reserved for future open-dialog initialisation.
   */
  ngOnChanges(changes: SimpleChanges) {

    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** ImportExcelDlgComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //console.log('*** CreateNewDocumentComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {

        case 'paramsMap':
          this.processDateField = this.paramsMap.find(e => (e.fieldType) == 'dat') ? true : false;
          break;

        case 'document':
          if (change.currentValue) {
            this.docItems = [... (this.document.items || [])];

            switch (this.document.DOCTYPE) {

              case 1:
              case 12:

              case 4:
              case 5:
                break;

              case 7:
                break;

              case 2:
              case 3:
              case 10:
                this.diU4ETKAColumnName = 'Инвойс';
                this.diText1ColumnLable = 'Дата поставки';
                break;
            }
          }
          break;

        case 'display':
          if (change.currentValue) {
          }
          break;
      }
    }
  }

  /** Clears runtime state and unsubscribes all active subscriptions. */
  ngOnDestroy(): void {
    console.log('*** ImportExcelDlgComponent in ngOnDestroy')

    this.docItems = [];
    this.columns = [];
    this.textSource = '';

    if (this._documentQuery) { this._documentQuery.unsubscribe(); }

    if (this._getProductQuery) { this._getProductQuery.unsubscribe(); }

    if (this._dataSourceQuery) { this._dataSourceQuery.unsubscribe(); }

    if (this._documentPostQuery) { this._documentPostQuery.unsubscribe(); }

    if (this._productInfoQuery) { this._productInfoQuery.unsubscribe(); }
  }

  /** Loads global settings. The import pipeline is driven by `onDataInsert` rather than a long-lived Subject. */
  ngOnInit(): void {
    console.log('*** ImportExcelDlgComponent in ngOnInit');

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

  }

  /** Called by PrimeNG dialog `(onShow)` — debug hook. */
  onShowDialog() {
    console.log('*** ImportExcelDlgComponent onShow: ');
  }

  /** Emits `false` via `displayChange` to close the dialog. */
  onHideDialog() {
    console.log('*** ImportExcelDlgComponent onHide');
    this.displayChange.emit(false);
  }

  /**
   * Persists the imported items to the API and emits `importIsDone`.
   * Replaces `document.items` with the current `docItems`, then calls
   * `deleteAllDocItemByDocId` followed by sequential PUT/POST for each item
   * (POST when `DOCITEMID === -2`, PUT otherwise).
   */
  onImportContent(event) {
    console.log('*** ImportExcelDlgComponent onImportContent');

    environment.production || console.table(this.docItems);

    this.document.items = [... this.docItems];
    this.blocked = true;

    this._documentPostQuery =
      concat(
        this.docApi.deleteAllDocItemByDocId(this.document.DOCID),

        from(this.document.items)
          .pipe(concatMap(di => (di.DOCITEMID == -2) ? this.docApi.postDocItemById(di) : this.docApi.putDocItemById(di))),

        of(true).pipe(map(data => {

          this.importIsDone.emit(this.docItems);
          this.display = false;
          this.blocked = false;
        }))
      )
        .subscribe();
  }

  /**
   * Advances the stepper to the import-result step and builds `docItems` from `dataSource`.
   * Iterates rows via `from + concatMap`, resolving each PRODCODE with `getProductByCode`,
   * then applies date parsing, duplicate handling (`addExisted`/`overwriteExisted`),
   * and VAT logic before pushing the final `DocItems` entry.
   */
  onDataInsert(event) {
    console.log('*** ImportExcelDlgComponent onDataInsert: ' + JSON.stringify(event));

    this.openFieldSelector = false;
    this.openImportResult = true;

    if (this.canInsertData) {
      if (this.clearBefore) {
        this.docItems = [];
      }
      /*
          { fieldName: 'DOCITEMPRODNAME', fieldLabel: 'Наименование товара', fieldIsMandatory: false, fieldType: 'str' },
          { fieldName: 'DOCITEMPRICE', fieldLabel: 'Цена товара', fieldIsMandatory: true, fieldType: 'num' },
          { fieldName: 'DOCITEMQTY', fieldLabel: 'Количество', fieldIsMandatory: true, fieldType: 'num' },
          { fieldName: 'DOCITEMVPRICE', fieldLabel: 'Цена в валюте', fieldIsMandatory: false, fieldType: 'num' },
          { fieldName: 'DOCITEMTEXT1', fieldLabel: 'Примечание 1', fieldIsMandatory: false, fieldType: 'str' },
          { fieldName: 'DOCITEMTEXT2', fieldLabel: 'Примечание 2', fieldIsMandatory: false, fieldType: 'str' },
          { fieldName: 'U4ETKA', fieldLabel: 'Учетка', fieldIsMandatory: false, fieldType: 'num' },
      */
      let prodCodeColumnName = (this.paramsMap.find(e => (e.fieldName) == 'PRODCODE') || {}).importColumnName || '';
      let diQuantityColumnName = (this.paramsMap.find(e => (e.fieldName) == 'DOCITEMQTY') || {}).importColumnName || '';
      let diU4etkaColumnName = (this.paramsMap.find(e => (e.fieldName) == 'U4ETKA') || {}).importColumnName || '';
      let diText1ColumnName = (this.paramsMap.find(e => (e.fieldName) == 'DOCITEMTEXT1') || {}).importColumnName || '';
      let diText2ColumnName = (this.paramsMap.find(e => (e.fieldName) == 'DOCITEMTEXT2') || {}).importColumnName || '';
      let diPriceColumnName = (this.paramsMap.find(e => (e.fieldName) == 'DOCITEMPRICE') || {}).importColumnName || '';
      let diVPriceColumnName = (this.paramsMap.find(e => (e.fieldName) == 'DOCITEMVPRICE') || {}).importColumnName || '';

      /*
       * So far we know, only DOCITEMTEXT1 and/or DOCITEMTEXT2 could be processed as Date
       * So, let's start from DOCITEMTEXT1 (ExcelDateToJSDate)
       * 
       *     'Pure Excel (44358)',
       *     'Pure Date (from JSON)',
       *     'Default Javascript (MM/DD/YYYY)',
       *     'More usefull (DD/MM/YYYY)',
       *     'Trick 1 (20210123 => 2021-01-23)',
       *     'Trick 2 (20210507 => 2021-07-05)',
       *     'Don\'t know, honestly'
       *
       *  Thanks to:
       *     https://www.npmjs.com/package/date-and-time#parsedatestring-arg-utc
       *
       *  Everything looks possible
       */

      let processType = this.dateProcessType.findIndex(s => s == this.selectedDateProcess);

      this.loading = true;

      this._dataSourceQuery =
        from(this.dataSource)
          .pipe(
            filter(row => { console.log(row[prodCodeColumnName]); return (row[prodCodeColumnName] || '').length > 3; }),
            concatMap(row => this.prodApi.getProductByCode(row[prodCodeColumnName], true, true).pipe(map(prod => {
              let product: Product = prod[0];
              environment.production || console.log('*** ImportExcelDlgComponent in row: ' + JSON.stringify(row));
              environment.production || console.log('*** ImportExcelDlgComponent in getProductByCode: ' + JSON.stringify(prod));

              if (product) {

                let prodQuantity: number = ((row[diQuantityColumnName] || '') == '') ? 1 : parseMyFloatEx(row[diQuantityColumnName]);

                let prodPrice: number = ((row[diPriceColumnName] || '') == '') ? null : parseMyFloatEx(row[diPriceColumnName]);
                let prodVPrice: number = ((row[diVPriceColumnName] || '') == '') ? null : parseMyFloatEx(row[diVPriceColumnName]);

                let u4etka: number = ((row[diU4etkaColumnName] || '') == '') ? null : parseMyFloatEx(row[diU4etkaColumnName]);

                let diText1: string = ((row[diText1ColumnName] || '') == '') ? null : row[diText1ColumnName];
                let diText2: string = ((row[diText2ColumnName] || '') == '') ? null : row[diText2ColumnName];

                let shippingDate = new Date();

                if (this.processDateField && (diText1ColumnName != '') && diText1) {
                  console.log('*** ImportExcelDlgComponent in if : ' + diText1ColumnName + '/' + diText1 + '/' + processType);
                  switch (processType) {

                    case 0:
                      shippingDate = ExcelDateToJSDate(parseInt(diText1));
                      console.log('*** ImportExcelDlgComponent in data processType 0: ' + diText1 + '/' + shippingDate);
                      break;

                    case 1:
                    case 2:
                      console.log('*** ImportExcelDlgComponent in data processType 1: ' + diText1 + '/' + shippingDate);
                      shippingDate = new Date(diText1);
                      break;

                    case 3: // DD/MM/YYYY
                      shippingDate = new Date(date.parse(diText1, 'DD/MM/YYYY'));
                      console.log('*** ImportExcelDlgComponent in data processType 3: ' + diText1 + '/' + shippingDate);
                      break;

                    case 4: // DD.MM.YYYY
                      shippingDate = new Date(date.parse(diText1, 'DD.MM.YYYY'));
                      console.log('*** ImportExcelDlgComponent in data processType 4: ' + diText1 + '/' + shippingDate);
                      break;

                    case 5: // 20210123
                      shippingDate = new Date(date.parse(diText1, 'YYYYMMDD'));
                      console.log('*** ImportExcelDlgComponent in data processType 5: ' + diText1 + '/' + shippingDate);
                      break;

                    case 6: // 20212301
                      shippingDate = new Date(date.parse(diText1, 'YYYYDDMM'));
                      console.log('*** ImportExcelDlgComponent in data processType 6: ' + diText1 + '/' + shippingDate);
                      break;

                    case 7: // 20210723
                      shippingDate = new Date(date.parse(diText1, 'YYYY-MM-DD'));
                      console.log('*** ImportExcelDlgComponent in data processType 7: ' + diText1 + '/' + shippingDate);
                      break;

                    default:
                      break;
                  }
                }

                /*
                 * if both Vprice and PRice import columns defined, VPrice has higher priority
                 */

                if (prodVPrice) {
                  prodPrice = prodVPrice * this.document.DOCRATEVAL;
                }

                prodVPrice = prodPrice / this.document.DOCRATEVAL;

                // Match on the resolved product, not the spreadsheet row: `row` is keyed by the
                // sheet's own column headers, so `row.code` was undefined and this never matched.
                let existed = this.docItems.findIndex(e => e.PRODID == product.PRODID);

                if ((existed > -1) && this.addExisted) {
                  this.docItems[existed].DOCITEMQTY += prodQuantity;
                  this.docItems[existed].U4ETKA = u4etka;

                  // Types 2/3/10 take their price from the product lookup, so the sheet may carry
                  // none (prodPrice null); keep the line's own price instead of zeroing its sums.
                  const addPrice = prodPrice ?? this.docItems[existed].DOCITEMPRICE;

                  this.docItems[existed].DOCITEMSUM = this.docItems[existed].DOCITEMQTY * addPrice;
                  this.docItems[existed].DOCITEMVSUM = addPrice / this.document.DOCRATEVAL * this.docItems[existed].DOCITEMQTY;
                  this.docItems[existed].DOCITEMSUMU4 = u4etka * this.docItems[existed].DOCITEMQTY;

                  this.docItems[existed].DOCITEMTEXT1 = diText1 ?? this.docItems[existed].DOCITEMTEXT1;
                  this.docItems[existed].DOCITEMTEXT2 = diText2 ?? this.docItems[existed].DOCITEMTEXT2;

                  this.docItems[existed].DOCITEMPOSHIPPINGDATE = shippingDate;

                  console.log('*** ImportExcelDlgComponent add to existed: ' + JSON.stringify(this.docItems[existed]));
                } else
                  if ((existed > -1) && this.overwriteExisted) {
                    const owPrice = prodPrice ?? this.docItems[existed].DOCITEMPRICE;

                    this.docItems[existed].DOCITEMQTY = prodQuantity;
                    this.docItems[existed].DOCITEMPRICE = owPrice;
                    this.docItems[existed].DOCITEMVPRICE = owPrice / this.document.DOCRATEVAL;
                    this.docItems[existed].U4ETKA = u4etka;

                    this.docItems[existed].DOCITEMSUM = prodQuantity * owPrice;
                    this.docItems[existed].DOCITEMVSUM = owPrice / this.document.DOCRATEVAL * prodQuantity;
                    this.docItems[existed].DOCITEMSUMU4 = u4etka * prodQuantity;

                    this.docItems[existed].DOCITEMTEXT1 = diText1 ?? this.docItems[existed].DOCITEMTEXT1;
                    this.docItems[existed].DOCITEMTEXT2 = diText2 ?? this.docItems[existed].DOCITEMTEXT2;

                    this.docItems[existed].DOCITEMPOSHIPPINGDATE = shippingDate;

                    console.log('*** ImportExcelDlgComponent update existed: ' + JSON.stringify(this.docItems[existed]));
                  } else {

                    let maxRowIndex = 0;

                    let newDi: DocItems = {

                      DOCITEMID: -2,
                      DOCID: this.document.DOCID,
                      DOCTYPE: this.document.DOCTYPE,
                      PRODID: product.PRODID,
                      PRODCODE: product.PRODCODE,
                      DOCITEMPRODNAME: product.PRODNAME,
                      DOCITEMPRICE: prodPrice,
                      DOCITEMQTY: prodQuantity,
                      DOCITEMVPRICE: prodPrice / this.document.DOCRATEVAL,
                      DOCITEMCURRID: this.document.DOCCURRID,
                      DOCITEMCRATE: this.document.DOCRATEVAL,
                      PAYITEMCLIID: maxRowIndex + 10,
                      PAYITEMDEPID: null,
                      DOCITEMTEXT1: diText1,
                      DOCITEMTEXT2: diText2,
                      CURCODE: '',
                      U4ETKA: u4etka,
                      PRODVAT: product.PRODVAT ?? this.globalSettinsData.vatValue,
                      cloneIndex: 0,

                      DOCITEMSUM: prodPrice * prodQuantity,
                      DOCITEMVSUM: prodPrice / this.document.DOCRATEVAL * prodQuantity,
                      DOCITEMSUMU4: u4etka * prodQuantity,
                      DOCITEMPOSHIPPINGDATE: shippingDate
                    };

                    console.log('*** ImportExcelDlgComponent in getProductByCode 2: ' + JSON.stringify(newDi));

                    switch (this.document.DOCTYPE) {

                      case 1:
                      case 12:
                        newDi.DOCITEMPRICEWOVAT = round1(prodPrice / ((100.0 + newDi.PRODVAT) / 100.0));
                        newDi.DOCITEMPRICE = round2(newDi.DOCITEMPRICEWOVAT * ((100.0 + newDi.PRODVAT) / 100.0));
                        newDi.DOCITEMVPRICE = round2(newDi.DOCITEMPRICE / this.document.DOCRATEVAL);
                        newDi.DOCITEMSUMWOVAT = newDi.DOCITEMQTY * newDi.DOCITEMPRICEWOVAT;
                        newDi.DOCITEMSUM = newDi.DOCITEMQTY * newDi.DOCITEMPRICE;
                        newDi.DOCITEMVSUM = newDi.DOCITEMQTY * newDi.DOCITEMVPRICE;

                      case 4:
                      case 5:
                        this.docItems.forEach(pp => { maxRowIndex = Math.max(maxRowIndex, pp.PAYITEMCLIID) });

                        this.docItems.push({ ...newDi, PAYITEMCLIID: maxRowIndex + 10 });
                        break;

                      case 7:
                        this.docItems.forEach(pp => { maxRowIndex = Math.max(maxRowIndex, pp.PAYITEMCLIID) });

                        this.docItems.push({ ...newDi, PAYITEMCLIID: maxRowIndex + 10 });
                        break;

                      case 2:
                      case 3:
                      case 10: {

                        // Reserve the line synchronously, before the price lookup returns. The outer
                        // concatMap moves to the next row as soon as getProductByCode completes, so
                        // inserting only inside the async callback let a repeated product in the same
                        // sheet miss its first occurrence and become a duplicate. The lookup below
                        // fills in the price on this same object.
                        this.docItems.forEach(pp => { maxRowIndex = Math.max(maxRowIndex, pp.PAYITEMCLIID) });

                        const reserved: DocItems = { ...newDi, PAYITEMCLIID: maxRowIndex + 10 };
                        this.docItems.push(reserved);

                        this._productInfoQuery =
                          this.prodApi.getProdInfoById(
                            newDi.PRODID,
                            this.document.DOCMANCLIID,
                            this.document.DOCCURRID,
                            //
                            // It depends on document type ????
                            //
                            0,
                            0,
                            0,
                            //
                            // Is it on actual date or document date
                            // Could depends on....
                            // TODO: We have to divide the price and reservation info
                            // One need on actual date, but some on document date....
                            // Complicated.... 
                            //
                            // new Date(this.inputDocument.DOCDATE)
                            new Date(this.document.DOCDATE)
                          )
                            .pipe(map(data => {

                              // Quantity is read from the reserved line, not newDi: a repeated row may
                              // already have accumulated onto it while this lookup was in flight.
                              reserved.DOCITEMPRICE = (prodVPrice || data[0].PRODINPRICE) * this.document.DOCRATEVAL;
                              reserved.DOCITEMVPRICE = (prodVPrice || data[0].PRODINPRICE);
                              reserved.DOCITEMSUM = reserved.DOCITEMQTY * reserved.DOCITEMPRICE;
                              reserved.DOCITEMVSUM = reserved.DOCITEMQTY * reserved.DOCITEMVPRICE;

                              reserved.DOCITEMTEXT1 = shippingDate.toDateString();

                            })).subscribe({
                              // Same outcome as before the line was reserved: a failed price lookup
                              // leaves no half-priced line behind.
                              error: (err) => {
                                console.error('*** ImportExcelDlgComponent product info lookup failed, line dropped: ', err);
                                const i = this.docItems.indexOf(reserved);
                                if (i > -1) { this.docItems.splice(i, 1); }
                              }
                            });
                        break;
                      }
                    }

                    // console.table(this.document.items);
                  }
              }
            }))),
            finalize(() => (this.loading = false))
          )
          .subscribe();
    }
  }

  /** Upload progress event handler — debug hook. */
  onProgress(event) {
    console.log('*** ImportExcelDlgComponent onProgress: ' + JSON.stringify(event));

  }

  /**
   * Extracts column headers from the first row of `dataSource` into `columns`
   * and advances the stepper from the file-selector to the field-selector step.
   */
  processDataSource() {
    this.columns = [];

    console.log('*** ImportExcelDlgComponent reader.sheet_to_json: ' + JSON.stringify(this.dataSource));
    if ((this.dataSource || []).length > 0) {

      console.log('*** ImportExcelDlgComponent reader.sheet_to_json[0]: ' + JSON.stringify(this.dataSource[0]));
      for (const keys in this.dataSource[0]) {
        console.log('*** ImportExcelDlgComponent reader.data.keys: ' + keys);

        this.columns.push(keys);
      }

      this.openFileSelector = false;
      this.openFieldSelector = true;
    } else {
      console.log('*** ImportExcelDlgComponent reader.sheet_to_json: SEEMS EMPTY');

    }
  }

  /** Tab-change event handler — debug hook. */
  onTabChange(event) {
    console.log('*** ImportExcelDlgComponent onProgress: ' + JSON.stringify(event));

  }

  /**
   * Parses `textSource` as tab/CSV text (using `parseTextBuffer` with the current
   * `textDelimiter`) into `dataSource`, then calls `processDataSource`.
   */
  onInsertText(event) {
    console.log('*** ImportExcelDlgComponent onInsertText: ');

    this.dataSource = parseTextBuffer(this.textSource, this.textDelimiter);

    //    this.dataSource = txtToJSON({ data: this.textSource });
    console.log('*** ImportExcelDlgComponent reader.onload: ' + JSON.stringify(this.dataSource))
    this.processDataSource();
  }

  /**
   * Handles the native `<input type="file">` change event.
   * Reads the first sheet of the uploaded `.xlsx` file via `XLSX.utils.sheet_to_json`
   * and calls `processDataSource` to extract column headers.
   */
  onFileChange(event) {
    console.log('*** ImportExcelDlgComponent onBasicUpload: ');

    const target: DataTransfer = <DataTransfer>(event.target);

    this.dataSource = [];

    if (target.files.length !== 1) throw new Error('Cannot use multiple files');

    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      /* read workbook */
      const bstr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });

      /* grab first sheet */
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      console.log('*** ImportExcelDlgComponent reader.onload: ' + wsname)

      this.dataSource = [...XLSX.utils.sheet_to_json(ws)];

      this.processDataSource();

      /* save data */
      // this.data = <AOA>(XLSX.utils.sheet_to_json(ws, { header: 1 }));
      // console.log(this.data);
    };
    reader.readAsBinaryString(target.files[0]);
  }

  /** Handles the PrimeNG `FileUpload` upload event (alternative upload path). */
  onBasicUpload(event) {
    console.log('*** ImportExcelDlgComponent onBasicUpload: ' + JSON.stringify(event));

    const target: DataTransfer = <DataTransfer>(event.files);

    if (target.files.length !== 1) throw new Error('Cannot use multiple files');
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      /* read workbook */
      const bstr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });

      /* grab first sheet */
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      console.log('*** ImportExcelDlgComponent reader.onload: ' + wsname)

      /* save data */
      // this.data = <AOA>(XLSX.utils.sheet_to_json(ws, { header: 1 }));
      // console.log(this.data);
    };
    reader.readAsBinaryString(target.files[0]);
  }

  /** Handles text-type radio button selection; updates `textDelimiter` and resets parsed state. */
  onTextTypeClick(event) {
    console.log('*** ImportExcelDlgComponent onTextTypeClick: ' + JSON.stringify(event));

    switch (this.textType) {
      case 'tab': this.textDelimiter = '\t'; break;
      case 'csv': this.textDelimiter = ';'; break;
      default: this.textDelimiter = '\t'; break;
    }
    this.columns = [];
    this.dataSource = [];
  }
}
